/**
 * useOrgChartMemberDetail Hook (V55.14)
 *
 * 조직도에서 구성원 클릭 시 상세 정보를 조회하는 Hook
 *
 * @module shared/hooks/useOrgChartMemberDetail
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

/**
 * 조직도 구성원 상세 정보 타입
 */
export interface OrgChartMemberDetailData {
  // 기본 정보
  id: string;
  nameKo: string;
  email: string;
  phoneNumber: string;
  photoPath: string;

  // 소속 정보
  orgUnitId: string;
  orgUnitName: string;

  // 직무 정보
  positionTitle: string;
  jobTitle: string;

  // 좌석 정보
  seatCode: string;      // "A-1" 형식
  seatLabel: string;     // "A구역 1번" 형식

  // V64: 현재 근무 상태
  currentWorkStatus: string;
}

interface GetOrgChartMemberDetailResponse {
  success: boolean;
  member: OrgChartMemberDetailData | null;
  message?: string;
}

/**
 * Hook 반환 타입
 */
export interface UseOrgChartMemberDetailResult {
  member: OrgChartMemberDetailData | null;
  isLoading: boolean;
  error: string | null;
  fetchMember: (targetUserId: string) => Promise<void>;
  reset: () => void;
}

/**
 * 조직도 구성원 상세 정보 조회 Hook
 */
export function useOrgChartMemberDetail(): UseOrgChartMemberDetailResult {
  const [member, setMember] = useState<OrgChartMemberDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 구성원 상세 정보 조회
   */
  const fetchMember = useCallback(async (targetUserId: string) => {
    if (!targetUserId) {
      setError('targetUserId가 필요합니다.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (!functions) {
        throw new Error('Firebase Functions not initialized');
      }

      const fn = httpsCallable<{ targetUserId: string }, GetOrgChartMemberDetailResponse>(
        functions,
        'getOrgChartMemberDetail'
      );

      const result = await fn({ targetUserId });

      if (result.data.success && result.data.member) {
        setMember(result.data.member);
      } else {
        setError(result.data.message || '직원 정보 조회에 실패했습니다.');
        setMember(null);
      }
    } catch (err: any) {
      console.error('[useOrgChartMemberDetail] 조회 실패:', err);
      setError(err?.message || '직원 정보 조회 중 오류가 발생했습니다.');
      setMember(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * 상태 초기화
   */
  const reset = useCallback(() => {
    setMember(null);
    setIsLoading(false);
    setError(null);
  }, []);

  return {
    member,
    isLoading,
    error,
    fetchMember,
    reset,
  };
}
